// ABOUTME: Jotai atoms holding the interactive product tour's run flag, steps and current step.
// ABOUTME: Shared between the menu trigger, the section picker and the GuidedTour renderer.
import { useUser } from "@flanksource-ui/context";
import { atom, useSetAtom } from "jotai";
import { type Step } from "react-joyride";
import {
  buildTourSteps,
  buildTouchpointSteps,
  type TourSection
} from "./guidedTourSteps";

export const tourRunAtom = atom(false);
export const tourStepIndexAtom = atom(0);
export const tourMenuOpenAtom = atom(false);
export const tourStepsAtom = atom<Step[]>([]);

/**
 * The walkthrough follows page access; submitting a run is authorized by the server.
 */
export function useCanRunPlaybooks() {
  const { access } = useUser();
  return access?.playbook?.read === "all" || access?.playbook?.read === "some";
}

/**
 * Returns a callback that opens the tour section picker.
 */
export function useStartTour() {
  const setMenuOpen = useSetAtom(tourMenuOpenAtom);

  return () => setMenuOpen(true);
}

/**
 * Returns a callback that starts the tour for a chosen section (or the full
 * tour), closing the picker.
 */
export function useStartTourSection() {
  const setMenuOpen = useSetAtom(tourMenuOpenAtom);
  const setSteps = useSetAtom(tourStepsAtom);
  const setStepIndex = useSetAtom(tourStepIndexAtom);
  const setRun = useSetAtom(tourRunAtom);
  const canRunPlaybooks = useCanRunPlaybooks();

  return (section: TourSection) => {
    setSteps(buildTourSteps(section, { canRunPlaybooks }));
    setStepIndex(0);
    setMenuOpen(false);
    setRun(true);
  };
}

/**
 * Returns a callback that starts the minimal guided walk reaching a single
 * checklist touchpoint.
 */
export function useStartTouchpoint() {
  const setMenuOpen = useSetAtom(tourMenuOpenAtom);
  const setSteps = useSetAtom(tourStepsAtom);
  const setStepIndex = useSetAtom(tourStepIndexAtom);
  const setRun = useSetAtom(tourRunAtom);

  return (touchpoint: string) => {
    const steps = buildTouchpointSteps(touchpoint);
    if (steps.length === 0) {
      return;
    }
    setSteps(steps);
    setStepIndex(0);
    setMenuOpen(false);
    setRun(true);
  };
}
